import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import type { Executive } from '../api/contract'
import { relativeTime } from '../lib/time'
import { useShare, toShareTarget } from './ShareModal'
import './RecentlyViewed.css'
import { InfoTip } from './InfoTip'
import { Link } from 'react-router-dom'

function Avatar() {
  return (
    <div className="face sm" aria-hidden="true">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
        <circle cx="10" cy="6.8" r="3.4" />
        <path d="M3.6 17.5c0-3.5 2.9-6.1 6.4-6.1s6.4 2.6 6.4 6.1z" />
      </svg>
    </div>
  )
}

function ExecutiveCard({ exec }: { exec: Executive }) {
  const share = useShare()
  return (
    <div className="rc">
      <span className="mt tnum">{exec.match_score}% Match</span>
      <div className="rc-t">
        <Avatar />
        <div className="rc-i">
          {/* display_name is already redacted server-side — never render legal_name
              directly, it is absent unless identity has actually been released */}
          <div className="rc-n">{exec.display_name}</div>
          <div className="rc-m">
            <span className="ex-i">{exec.reference_code}</span>
            {exec.last_viewed_at && (
              <span className="when">Viewed {relativeTime(exec.last_viewed_at)}</span>
            )}
          </div>
        </div>
      </div>
      <div className="rc-b">
        <Link className="btn" to="/recommended">View Profile</Link>
        <span className="sp" />
        <button className="btn" type="button" onClick={() => share(toShareTarget(exec))}>Share</button>
        <Link className="btn chatbtn" to="/conversations">
          <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
            <path d="M3.5 5.5A1.5 1.5 0 0 1 5 4h10a1.5 1.5 0 0 1 1.5 1.5v6A1.5 1.5 0 0 1 15 13H8l-4.5 3.2z" />
          </svg>
          Chat
        </Link>
      </div>
    </div>
  )
}

const TABS = [
  { key: 'viewed', label: 'Recently Viewed' },
  { key: 'shortlisted', label: 'Shortlisted' },
  { key: 'shared', label: 'Shared With Me' },
] as const

const FILTERS = ['All', 'CFO', 'COO', 'Board'] as const

export function RecentlyViewed() {
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>('viewed')
  const [filter, setFilter] = useState<string>('All')

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['executives', 'recent', tab],
    queryFn: () => api.getRecentExecutives(tab),
  })

  // filter against a modelled field, never a substring of the display name
  const visible = (data?.items ?? []).filter(
    (e) => filter === 'All' || e.role_family === filter,
  )

  /** Arrow-key navigation between tabs, as the tablist pattern requires. */
  function onTabKey(e: React.KeyboardEvent, index: number) {
    const delta = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
    if (!delta) return
    e.preventDefault()
    const next = TABS[(index + delta + TABS.length) % TABS.length]
    if (!next) return
    setTab(next.key)
    document.getElementById(`tab-${next.key}`)?.focus()
  }

  return (
    <section className="card sec" style={{ padding: '18px 0 0' }}>
      <h2 className="h2" style={{ padding: '0 20px' }}>Recently Viewed &amp; Shortlisted Executives <InfoTip tip="recentlyViewed" /></h2>

      <div className="tabbar" style={{ marginTop: 8 }}>
        {/* role=tab is meaningless without a tablist parent; roving tabindex
            keeps the group a single tab stop, per the WAI-ARIA pattern */}
        <div role="tablist" aria-label="Executive lists" style={{ display: 'flex', gap: 26 }}>
          {TABS.map((t, i) => (
            <button
              key={t.key} id={`tab-${t.key}`} className="tab" role="tab" type="button"
              aria-selected={tab === t.key}
              aria-controls="recent-panel"
              tabIndex={tab === t.key ? 0 : -1}
              onKeyDown={(e) => onTabKey(e, i)}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="fchips">
          {FILTERS.map((f) => (
            <button
              key={f} className="fc" type="button"
              aria-pressed={filter === f}
              onClick={() => setFilter(f)}
            >
              {f}
            </button>
          ))}
          <button
            className="fc" type="button"
            aria-pressed={filter === 'New Activity'}
            onClick={() => setFilter((c) => (c === 'New Activity' ? 'All' : 'New Activity'))}
          >
            New Activity<span className="cbadge">4</span>
          </button>
        </div>
      </div>

      {isPending && (
        <div className="rcs" aria-busy="true" aria-live="polite">
          <span className="sr-only">Loading executives</span>
          {[0, 1, 2].map((i) => <div key={i} className="rc rc-skeleton" />)}
        </div>
      )}

      {isError && (
        <div className="rc-msg" role="alert">
          <b>Could not load executives</b>
          {error && typeof error === 'object' && 'message' in error
            ? String((error as { message: unknown }).message)
            : 'Something went wrong.'}
          <div style={{ marginTop: 12 }}>
            <button className="btn" type="button" onClick={() => void refetch()}>Try again</button>
          </div>
        </div>
      )}

      {data && data.items.length === 0 && (
        <div className="rc-msg">
          <b>Nothing here yet</b>
          Executives you or your team open, shortlist, or share appear here.
        </div>
      )}

      {data && data.items.length > 0 && visible.length === 0 && (
        <div className="rc-msg">
          <b>No executives match this filter</b>
          {data.items.length} hidden by the “{filter}” filter.
        </div>
      )}

      {visible.length > 0 && (
        <div className="rcs" id="recent-panel" role="tabpanel">
          {visible.map((exec) => <ExecutiveCard key={exec.id} exec={exec} />)}
        </div>
      )}
    </section>
  )
}
